import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { config } from '../../../app-confi/app-config';
import { Point3 } from '../../../common/models/point.type';
import { usePrevious } from '../../../common/react-helpers';
import { Building } from '../../../confi-state';
import { ServiceManager } from '../../../services/service-manager';
import NumberArrowSelector from '../../Common/Inputs/NumberArrowSelector/NumberArrowSelector';
import { InitialProps } from '../Common/BuildingInitialPropertiesDialog/init-props';
import { ColumnService } from './column-service';
import { columnType } from './models/column-type.enum';
import { getDefaultPropsRowhouse, PropsRowhouse } from './models/props-config';
import styles from './Rowhouse.module.css';
import { RowhouseService } from './rowhouse.service';

const buildingChange = (buildingProps: PropsRowhouse, currentBuilding: Building, onChangeBuilding) => {
    onChangeBuilding(currentBuilding.projectId, currentBuilding.siteId, { ...currentBuilding, props: { ...buildingProps } }, true);
};

const Rowhouse = ({ currentBuilding, buildingScaleDimensions, previousBuildingDimensions, initProps, onChangeBuilding }: { currentBuilding: Building, buildingScaleDimensions: Point3, previousBuildingDimensions: Point3, initProps: InitialProps, onChangeBuilding: (projectId: number | string, siteId: number | string, building: Building, saveToDb: boolean) => void }) => {

    const [buildingLengthUpdatedByColumns, setBuildingLengthUpdatedByColumns] = useState<boolean>(
false
    );

    const [buildingProps, setBuildingProps] = useState<PropsRowhouse>(
        undefined
    );
    const previousBuildingScaleDimensions = usePrevious<Point3>(buildingScaleDimensions);

    const maxLength = 1000;
    const minLength = 3;

    const recomputeColumns = useCallback((computeAmounts: boolean, bp: PropsRowhouse) => {
        let b = { ...bp };
        if (computeAmounts) {
            const amounts = RowhouseService.getAmountsByAvailableBays(b);
            b = { ...b, ...amounts };
        }

        const columnService = new ColumnService();

        b.parts = columnService.createPossibleColumnDefinitions(
            {
                amountOfHSColumns: b.amount_H_S_Columns,
                amountOfHM1M2Columns: b.amount_H_M1M2_Columns,
                amountOfHLColumns: b.amount_H_L_Columns,
                middleFloors: b.middleFloors,
            }
        );

        b.parts = ServiceManager.babylonService.rowhouseBabylonService.mapPartsTo3D(
            b.parts
        );

        b.totalAmountOfAllUnits = columnService.getTotalAmountOfAllUnits(b.parts);

        if (b.parts) {
            const columns = b.parts.map(s => s.columns).flat().filter(c => c !== undefined);
            b.info = ServiceManager.buildingHelperService.computeColumnInfo(columns);
        }
        setBuildingProps({ ...b });
        buildingChange({ ...b }, currentBuilding, onChangeBuilding);
    }, [currentBuilding, onChangeBuilding]);

    useEffect(() => {
        if (currentBuilding.props) {
            setBuildingProps({ ...currentBuilding.props as PropsRowhouse });
            // migrate
            const columns = (currentBuilding.props as any).columns;
            if (columns !== undefined) {
                delete (currentBuilding.props as any).columns;
                recomputeColumns(true, currentBuilding.props as PropsRowhouse);
            }
        }
    }, [currentBuilding.props, recomputeColumns])

    useEffect(() => {
        if (currentBuilding && !currentBuilding.props) {
            const props = getDefaultPropsRowhouse(initProps);
            if (previousBuildingDimensions && (previousBuildingDimensions[0] !== 0 && previousBuildingDimensions[1] !== 0 && previousBuildingDimensions[2] !== 0)) {
                props.buildingLength = ServiceManager.utilityService.keepInBounds(previousBuildingDimensions[0], minLength, maxLength);
            }
            recomputeColumns(true, { ...props });
        }
    }, [buildingProps, currentBuilding, previousBuildingDimensions, onChangeBuilding, recomputeColumns])

    useEffect(() => {
        if (buildingScaleDimensions && buildingProps && (
            buildingScaleDimensions[0] !== previousBuildingScaleDimensions[0]
        ) && buildingScaleDimensions[0] !== 0
        ) {

            const b = { ...buildingProps };

            const updatedLength = buildingProps.buildingLength !== buildingScaleDimensions[0];

            if (updatedLength) {
                b.buildingLength = ServiceManager.utilityService.keepInBounds(buildingScaleDimensions[0], minLength, maxLength);
                recomputeColumns(true, b);
            }
        }
    }, [buildingScaleDimensions, previousBuildingScaleDimensions, buildingProps, recomputeColumns]);


    const handleColumnTypeChange = (increment: boolean, type: columnType) => {
        const bp = { ...buildingProps };
        switch (type) {
            case columnType.H_S:
                bp.amount_H_S_Columns = increment ? bp.amount_H_S_Columns + 1 : bp.amount_H_S_Columns - 1;
                break;
            case columnType.H_L:
                bp.amount_H_L_Columns = increment ? bp.amount_H_L_Columns + 1 : bp.amount_H_L_Columns - 1;
                break;
            case columnType.H_M1M2:
                bp.amount_H_M1M2_Columns = increment ? bp.amount_H_M1M2_Columns + 1 : bp.amount_H_M1M2_Columns - 1;
                break;
            default:
                alert('unknown value type provided');
                break;
        }
        recomputeColumns(false, bp);
    }

    const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>, type: 'middleFloors') => {
        const bp = { ...buildingProps };
        const value = (e.target as any).value as any;
        switch (type) {
            case 'middleFloors':
                bp.middleFloors = +value;
                bp.buildingHeight = bp.middleFloors * config.rowhouseFloorHeight;
                recomputeColumns(false, bp);
                break;
            default:
                alert('unknown value type provided');
                break;
        }
    }

    const handleChange = (e: React.FocusEvent<HTMLInputElement, Element>, type: 'length') => {
        const bp = { ...buildingProps };
        const value = +(e.target as any).value as number;
        switch (type) {
            case 'length':
                bp.buildingLength = ServiceManager.utilityService.keepInBounds(value, minLength, maxLength);
                (e.target as HTMLInputElement).value = bp.buildingLength.toString();
                if (!buildingLengthUpdatedByColumns) {
                    recomputeColumns(true, bp);
                } else {
                    setBuildingLengthUpdatedByColumns(false);
                }
                break;
            default:
                alert('unknown value type provided');
                break;
        }
    }

    return (
        <div className={styles.Rowhouse} data-testid="Rowhouse">
            {buildingProps &&
                <>
                    <div className="group">
                        <h2 className="greyed-out-text">Kolommen aanpassen</h2>
                        <div>
                            <div className={'dimensions ' + styles.columnNr}>
                                <NumberArrowSelector
                                    label="S"
                                    min={0}
                                    max={100}
                                    value={buildingProps.amount_H_S_Columns}
                                    increment={() => handleColumnTypeChange(true, columnType.H_S)}
                                    decrement={() => handleColumnTypeChange(false, columnType.H_S)}
                                />
                                <NumberArrowSelector
                                    label="M"
                                    min={0}
                                    max={100}
                                    value={buildingProps.amount_H_M1M2_Columns}
                                    increment={() => handleColumnTypeChange(true, columnType.H_M1M2)}
                                    decrement={() => handleColumnTypeChange(false, columnType.H_M1M2)}
                                />
                                <NumberArrowSelector
                                    label="L"
                                    min={0}
                                    max={100}
                                    value={buildingProps.amount_H_L_Columns}
                                    increment={() => handleColumnTypeChange(true, columnType.H_L)}
                                    decrement={() => handleColumnTypeChange(false, columnType.H_L)}
                                />
                            </div>
                            <div className="extras">
                            </div>
                        </div>
                    </div>
                    <div className="group">
                        <h2 className="greyed-out-text">
                            Gebouw parameters
                        </h2>
                        <div className={styles.formControl}>
                            <FormControl size="medium" fullWidth>
                                <InputLabel id={"floors-selection-label"}>Verdiepingen</InputLabel>
                                <Select
                                    labelId={"floors-selection-label"}
                                    id={"floors-selection-id"}
                                    data-testid="floors-selection-id"
                                    value={buildingProps.middleFloors}
                                    onChange={(e) => {
                                        handleSelect(e as any, 'middleFloors')
                                    }}
                                    label="Verdiepingen"
                                >
                                    <MenuItem data-testid={'floors-selection-2'} value={2}>
                                        2
                                    </MenuItem>
                                    <MenuItem data-testid={'floors-selection-3'} value={3}>
                                        3
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                    </div>
                </>
            }
        </div>
    )
};

export default Rowhouse;